import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAdminToken } from "../services/authService";
import { useTheme } from "../ThemeContext";
import './version.css'
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";

const UpdateVersion = () => {
  const [newVersion, setNewVersion] = useState("");
  const [currentVersion, setCurrentVersion] = useState("Loading...");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useTheme();

  // Fetch current version
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await axios.get(
          "/s1/latest_version"
        );
        setCurrentVersion(response.data.version || "Unknown");
      } catch (err) {
        setError("Failed to fetch current version.");
      }
    };
    fetchVersion();
  }, []);

  // Handle version update
  const handleUpdateVersion = async () => {
    setMessage("");
    setError("");

    if (!newVersion.trim()) {
      setError("Please enter a valid version.");
      return;
    }

    setLoading(true);
    try {
      const token = getAdminToken();
      const response = await fetch(
        "/s1/admin/update_version",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ new_version: newVersion }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Unexpected error occurred!");
      } else {
        setCurrentVersion(data.version);
        setMessage("Version updated successfully!");
        setNewVersion("");
      }
    } catch (err) {
      setError(err.message || "Something went wrong, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        // Centred in the page on a desktop; at the top on a phone, where centring in 70vh
        // left the form below a screen of empty space.
        minHeight: { xs: 0, md: "70vh" },
        display: "flex",
        justifyContent: "center",
        alignItems: { xs: "flex-start", md: "center" },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2.5, sm: 4 },
          width: "100%",
          maxWidth: 400,
          backgroundColor: "var(--v2-surface)",
          color: "var(--v2-text)",
          textAlign: "center",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Update Software Version
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Current Version: {currentVersion}
        </Typography>

        <TextField
          label="New Version (e.g., 2.0.0)"
          variant="outlined"
          value={newVersion}
          onChange={(e) => setNewVersion(e.target.value)}
          fullWidth
          sx={{
            mb: 2,
            '& .MuiInputBase-input': {
              color: "var(--v2-text)", // text color
            },
            '& .MuiInputLabel-root': {
              color: "var(--v2-accent)", // label color
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: isDarkMode ? '#555' : '#ccc', // border color
              },
              '&:hover fieldset': {
                borderColor: "var(--v2-accent)", // hover border color
              },
              '&.Mui-focused fieldset': {
                borderColor: "var(--v2-accent)", // focused border color
              },
            },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleUpdateVersion}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Update Version"}
        </Button>

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default UpdateVersion;
